<script setup>
const route = useRoute();
const config = useRuntimeConfig();
const { t } = useI18n();

const mapHidden = ref(true);
const { data: storeRef } = await useApiFetch(
  `store-by-slug/${route.params.slug}`,
);

const structuredContentRef = computed(() => {
  return storeRef.value
    ? storeRef.value.content?.pages[0].frames[0].component.components
    : [];
});

const metaData = computed(() => {
  if (storeRef?.value?.meta) {
    return JSON.parse(storeRef.value.meta);
  }
  return null;
});

watchEffect(() => {
  if (metaData.value) {
    useHead({
      htmlAttrs: {
        lang: config.public.meta.lang,
      },
      title: metaData.value.title || storeRef?.value?.name,
      meta: [
        metaData.value.description && {
          name: "description",
          content: metaData.value.description,
        },
        metaData.value.robots && {
          name: "robots",
          content: metaData.value.robots,
        },
      ].filter(Boolean),
    });
  }
});
</script>

<template>
  <div v-if="storeRef">
    <PageMenuBase
      v-if="config.public.storeMenuId"
      :parent-id="parseInt(config.public.storeMenuId)"
      :max-levels="parseInt(1)"
    />
    <h1
      class="!text-3xl md:!text-4xl lg:!text-5xl font-bold text-center !m-5 !p-5 font-lora"
    >
      {{ storeRef.name }}
    </h1>

    <div class="container mt-5 px-3 lg:px-0">
      <div class="bg-brand-surface rounded-2xl">
        <div class="flex flex-wrap">
          <div class="w-full md:w-2/5 p-4 text-sm flex flex-col">
            <div class="bg-white rounded-2xl p-3">
              <div class="text-base uppercase mb-3">{{ t("contact") }}</div>
              {{ storeRef.address_string }}.<br />
              <a v-if="storeRef.phone" :href="`tel:${storeRef.phone}`">{{
                storeRef.phone
              }}</a
              ><br />
              <a v-if="storeRef.email" :href="`mailto:${storeRef.email}`">{{
                storeRef.email
              }}</a>
            </div>
            <div
              v-if="
                storeRef.opening_hours && Array.isArray(storeRef.opening_hours)
              "
              class="bg-white rounded-2xl p-3 mt-5 grow"
            >
              <div class="text-base uppercase mb-3">
                {{ t("open_hours") }}
              </div>
              <div
                v-for="day in storeRef.opening_hours"
                :key="day.key"
                class="flex flex-wrap"
              >
                <div class="w-1/2 p-1">{{ day.key }}</div>
                <div class="w-1/2 p-1">{{ day.value }}</div>
              </div>
            </div>

            <div class="text-right md:hidden">
              <span
                class="text-brand-link mt-3 inline-block"
                @click.prevent="mapHidden = !mapHidden"
                >{{ t("show_on_map") }}
                <i class="m-icon-mini-arrow text-10 ml-[5px]"></i
              ></span>
            </div>
          </div>
          <div
            v-if="storeRef.map"
            class="w-full md:w-3/5 p-4 md:block"
            :class="mapHidden ? 'hidden' : 'block'"
          >
            <!-- eslint-disable vue/no-v-html -->
            <div v-html="storeRef.map"></div>
            <!--eslint-enable-->
          </div>
        </div>
      </div>
    </div>

    <div v-if="structuredContentRef" class="container mt-5 px-3 lg:px-0">
      <template v-if="structuredContentRef">
        <template
          v-for="item in structuredContentRef"
          :key="JSON.stringify(item)"
        >
          <template v-if="item.tagName">
            <BaseTag
              :tag-name="item.tagName"
              :class="item.classes?.join(' ')"
              :attributes="item.attributes"
            >
              <CmsPageContentBlock
                v-bind="{
                  block: item,
                }"
              />
            </BaseTag>
          </template>
          <template v-else>
            <CmsPageContentBlock
              v-bind="{
                block: item,
              }"
            />
          </template>
        </template>
      </template>
    </div>
  </div>
</template>
